import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Chip, Input } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { FormInput, FormTextarea } from '../../base';
import CategorySelector from '../../base/selectors/CategorySelector';
import { glossaryFormSchema, GlossaryFormData } from './schemas';

interface GlossaryFormProps {
  onSubmit: (data: GlossaryFormData) => Promise<void>;
  defaultValues?: Partial<GlossaryFormData>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isLoadingGlossary?: boolean;
}

const GlossaryForm: React.FC<GlossaryFormProps> = ({
  onSubmit,
  defaultValues,
  onCancel,
  isSubmitting = false,
  isLoadingGlossary = false,
}) => {
  const { t } = useTranslation('admin');

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    resolver: zodResolver(glossaryFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      categoryId: '',
      aliases: [],
      tags: [],
      definition: '',
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, isLoadingGlossary, reset]);

  const {
    fields: aliasFields,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    control: control as any,
    name: 'aliases',
  });

  const {
    fields: tagFields,
    append: appendTag,
    remove: removeTag,
  } = useFieldArray({
    control: control as any,
    name: 'tags',
  });

  const [newAlias, setNewAlias] = useState('');
  const [newTag, setNewTag] = useState('');
  const categoryId = watch('categoryId');

  const handleFormSubmit: SubmitHandler<GlossaryFormData> = (data) => {
    onSubmit(data as GlossaryFormData);
  };

  const handleAddAlias = () => {
    if (newAlias.trim()) {
      appendAlias(newAlias.trim());
      setNewAlias('');
    }
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      appendTag(newTag.trim());
      setNewTag('');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormInput
        name="name"
        control={control}
        label={t('glossaries.modal.form.name.label')}
        placeholder={t('glossaries.modal.form.name.placeholder')}
        isRequired
        variant="bordered"
        isDisabled={isSubmitting || isLoadingGlossary}
      />

      <FormTextarea
        name="definition"
        control={control}
        label={t('glossaries.modal.form.definition.label')}
        placeholder={t('glossaries.modal.form.definition.placeholder')}
        variant="bordered"
        minRows={4}
        isRequired
        isDisabled={isSubmitting || isLoadingGlossary}
      />

      <CategorySelector
        value={categoryId}
        onChange={(value) => setValue('categoryId', value, { shouldValidate: true })}
        isDisabled={isSubmitting || isLoadingGlossary}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('glossaries.modal.form.aliases.label')}</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {aliasFields.map((field, index) => (
            <Chip key={field.id} onClose={() => removeAlias(index)} variant="flat" color="primary">
              {watch(`aliases.${index}`)}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('glossaries.modal.form.aliases.placeholder')}
            value={newAlias}
            onValueChange={setNewAlias}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddAlias();
              }
            }}
            isDisabled={isSubmitting || isLoadingGlossary}
            className="flex-1"
            variant="bordered"
          />
          <Button type="button" onPress={handleAddAlias} isIconOnly isDisabled={isSubmitting || isLoadingGlossary}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('glossaries.modal.form.tags.label')}</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {tagFields.map((field, index) => (
            <Chip key={field.id} onClose={() => removeTag(index)} variant="flat" color="secondary">
              {watch(`tags.${index}`)}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('glossaries.modal.form.tags.placeholder')}
            value={newTag}
            onValueChange={setNewTag}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            isDisabled={isSubmitting || isLoadingGlossary}
            className="flex-1"
            variant="bordered"
          />
          <Button type="button" onPress={handleAddTag} isIconOnly isDisabled={isSubmitting || isLoadingGlossary}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        {onCancel && (
          <Button variant="light" onPress={onCancel} isDisabled={isSubmitting || isLoadingGlossary}>
            {t('common.cancel')}
          </Button>
        )}
        <Button color="primary" type="submit" isLoading={isSubmitting || isLoadingGlossary}>
          {defaultValues?.name ? t('common.update') : t('common.create')}
        </Button>
      </div>
    </form>
  );
};

export default GlossaryForm;
